import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TESTIMONIALS } from '../content';

const Stars: React.FC<{ count: number; size?: number }> = ({
  count,
  size = 22,
}) => (
  <div className="flex gap-1">
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="#E0A82E">
        <path d="m12 2 2.9 6.2 6.8.7-5 4.6 1.4 6.7L12 17.8 5.9 20.2l1.4-6.7-5-4.6 6.8-.7L12 2Z" />
      </svg>
    ))}
  </div>
);

export const Testimonials: React.FC = () => {
  const [active, setActive] = useState(1); // middle one focused by default
  const n = TESTIMONIALS.length;
  const go = (dir: number) => setActive((a) => (a + dir + n) % n);

  const prev = TESTIMONIALS[(active - 1 + n) % n];
  const next = TESTIMONIALS[(active + 1) % n];
  const current = TESTIMONIALS[active];

  return (
    <section className="overflow-hidden px-4 md:px-8">
      <div className="relative mx-auto flex min-h-[300px] max-w-[1180px] items-center justify-center">
        {/* side peeks */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`prev-${prev.author}`}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 0.7, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="pointer-events-none absolute left-0 top-6 hidden w-[340px] rounded-3xl bg-[#EFE9DD]/70 p-7 lg:block"
          >
            <Stars count={prev.stars} size={18} />
            <p className="mt-3 line-clamp-5 text-sm leading-snug text-[#5C6068]">
              {prev.quote}
            </p>
            <p className="mt-2 text-sm text-[#9aa0a6]">— {prev.author}</p>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.div
            key={`next-${next.author}`}
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 0.7, x: 0 }}
            exit={{ opacity: 0, x: 14 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="pointer-events-none absolute right-0 top-6 hidden w-[340px] rounded-3xl bg-[#EFE9DD]/70 p-7 text-right lg:block"
          >
            <div className="flex justify-end">
              <Stars count={next.stars} size={24} />
            </div>
            <p className="mt-3 line-clamp-5 text-sm leading-snug text-[#5C6068]">
              {next.quote}
            </p>
            <p className="mt-2 text-sm text-[#9aa0a6]">— {next.author}</p>
          </motion.div>
        </AnimatePresence>

        {/* center card */}
        <div className="relative z-10 w-full max-w-[600px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
              className="relative rounded-3xl bg-white p-4 sm:p-8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.18)] md:p-10"
            >
              <Stars count={current.stars} />
              <p className="mt-5 text-base sm:text-xl leading-relaxed text-[#23262C] md:text-2xl">
                <span className="font-bold">“</span>
                {current.quote}
                <span className="font-bold">”</span>
              </p>
              <p className="mt-5 text-right text-lg font-bold text-[#23262C]">
                — {current.author}
              </p>
              {/* speech bubble tail */}
              <span className="absolute -bottom-3 left-12 hidden size-6 rotate-45 bg-white md:block" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="sm:mt-5 flex items-center justify-center gap-6 md:mt-10">
        <button
          aria-label="Previous"
          onClick={() => go(-1)}
          className="text-[#9aa0a6] transition-colors hover:text-[#23262C]"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5m6-7-7 7 7 7" />
          </svg>
        </button>
        <button
          aria-label="Next"
          onClick={() => go(1)}
          className="text-[#9aa0a6] transition-colors hover:text-[#23262C]"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14m-6-7 7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
};
